import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import { GlobalStyle } from '@/styles/GlobalStyle';
import { Theme as appTheme } from '@/Theme';

type Theme = 'dark' | 'light';

const applyDataTheme = (theme: Theme) => {
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-theme', theme);
};

// Dark-first: only an explicit stored 'light' opts out of dark.
const getInitialTheme = (): Theme => (typeof window !== 'undefined' && localStorage.theme === 'light' ? 'light' : 'dark');

// Set before first render so canvas/iframe consumers resolve the right tokens on load.
applyDataTheme(getInitialTheme());

const ThemeContext = createContext<{ theme: Theme; setTheme: (value: Theme) => void }>({
  theme: 'dark',
  setTheme: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  const setTheme = (value: Theme) => {
    applyDataTheme(value);
    setThemeState(value);
  };

  useEffect(() => {
    applyDataTheme(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <StyledThemeProvider theme={appTheme}>
        <GlobalStyle />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
